import { LogLevel, PublicClientApplication } from '@azure/msal-browser';
import type { Configuration, IPublicClientApplication } from '@azure/msal-browser';
import { config } from '../config';

/**
 * Auth-code + PKCE SPA configuration (spec §3). Tokens are cached in sessionStorage so a closed
 * tab drops the session; the API scope is requested at sign-in so the first API call is silent.
 */
export function buildMsalConfig(): Configuration {
  return {
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      // The origin the page was served from, not appBaseUrl: the app answers on both the custom
      // hostname and *.azurewebsites.net, and a redirect to the other origin loses the session.
      // Every served origin must be a SPA redirect URI on the app registration.
      redirectUri: `${config.origin}/`,
      postLogoutRedirectUri: `${config.origin}/`,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: {
      loggerOptions: {
        logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
        piiLoggingEnabled: false,
        loggerCallback: (level, message) => {
          if (level <= LogLevel.Error) console.error(message);
          else console.warn(message);
        },
      },
    },
  };
}

export const loginRequest = {
  scopes: config.apiScope ? ['openid', 'profile', 'email', config.apiScope] : ['openid', 'profile', 'email'],
};

export const apiTokenRequest = { scopes: config.apiScope ? [config.apiScope] : [] };

let instance: IPublicClientApplication | null = null;

export async function getMsalInstance(): Promise<IPublicClientApplication> {
  if (instance) return instance;
  const pca = new PublicClientApplication(buildMsalConfig());
  await pca.initialize();
  instance = pca;
  return pca;
}
